import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay, tap, catchError } from 'rxjs';
import { TenantService } from './tenant.service';
import { readExportSwitches } from '../utils/export-switch.utils';

/**
 * v1.6.1814 — per-tenant export switches (Asana 1219882772481516).
 *
 * "load GET api/v1/tenant/info once after sign in and keep
 *  is_general_export_enabled and is_report_export_enabled for the whole
 *  panel -> today only the dashboard and general settings call it"
 *
 * So this is a cache, not a fetcher-per-screen. Every export button in the
 * panel reads these two signals, and a button must not wait on its own HTTP
 * call to decide whether to render.
 *
 * ── Why the default is ON ───────────────────────────────────────────────
 *
 * Both signals start `true` and only an explicit `false` from the payload
 * turns one off. The alternative — start off, turn on when the read lands —
 * would blink every export button out of existence on every page load, and
 * would hide them permanently on any tenant whose backend predates the
 * flags. A wrongly-shown button is refused by the server with a message the
 * operator can read (see the 403 handling); a wrongly-hidden one is just
 * gone, with nothing to explain it.
 *
 * That is also why a FAILED read changes nothing: an unreachable
 * `/tenant/info` is not evidence that exports are off.
 */
@Injectable({ providedIn: 'root' })
export class ExportSwitchesService {
  private readonly _general = signal(true);
  private readonly _reports = signal(true);
  /** True once a read has actually landed. Lets a screen tell "on" from
   *  "not asked yet" without changing what it renders. */
  private readonly _loaded = signal(false);

  readonly generalEnabled = this._general.asReadonly();
  readonly reportExportsEnabled = this._reports.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  /** Convenience for templates that want the inverse. */
  readonly generalDisabled = computed(() => !this._general());
  readonly reportExportsDisabled = computed(() => !this._reports());

  private inFlight: Observable<any> | null = null;

  constructor(private http: HttpClient, private tenant: TenantService) {}

  /**
   * Idempotent. Safe to call from anywhere — the first call does the work,
   * every later one gets the same shared result, and once loaded it is a
   * no-op.
   */
  load(): Observable<any> {
    if (this._loaded()) return of(null);
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.http.get<any>(`${this.tenant.apiBaseUrl}/v1/tenant/info`).pipe(
      tap((res: any) => this.apply(res)),
      catchError(() => {
        // A failed read is not evidence that exports are off. Leave both
        // switches as they are and let the next navigation try again.
        this.inFlight = null;
        return of(null);
      }),
      shareReplay(1),
    );
    return this.inFlight;
  }

  /**
   * Feed the switches from a `/tenant/info` payload someone else already
   * fetched, so the panel does not make the same call twice.
   */
  apply(payload: any): void {
    const { general, reports } = readExportSwitches(payload);
    this._general.set(general);
    this._reports.set(reports);
    this._loaded.set(true);
  }

  /** Sign-out: the next tenant must not inherit this one's switches. */
  reset(): void {
    this._general.set(true);
    this._reports.set(true);
    this._loaded.set(false);
    this.inFlight = null;
  }
}
